import { Commit, GetterTree, StoreOptions } from 'vuex';
import { debounce } from 'lodash';
import i18n, { i18n_t } from '@/locales/i18n';

// 引入工具类
import { channelEditionMap } from '@/renderer/scripts/composition/channel';
import { IpcEvents } from '@/ipc-events';
import ConstantConfig, { RoleMap } from '@/renderer/scripts/utils/ConstantConfig';
import tips from '@/renderer/scripts/utils/tips';
import smh, { SMH } from '@/renderer/scripts/smh';
import ipc from '@/renderer/scripts/utils/ipc';
import userIns from '@/renderer/scripts/utils/user';
import { isDisabledAllAuth } from '@/renderer/scripts/utils/tools/index';
import { IS_PRIVATE } from '@/common/constant/platform';
interface PersonalInfo {
  userId: number;
  accountName: string;
  nickname: string;
  email: string;
  phoneNumber: string;
  avatar: string;
  role: string;
  comment: string;
  enabled: boolean;
  allowPersonalSpace: boolean;
  canManageTeams: number[];
  isTemporary: boolean;
  thirdPartyAuthList: {
    meeting: boolean;
    wechat: boolean;
  };
}

export interface UserAccount {
  userId: number;
  isNewUser: string;
  isOrgNewUser: string;
  isLogined: boolean;
  remember: boolean;
  multiOrg: boolean;
  organizationId: number;
  expired: boolean;
  editionFlag: string;
  editionConfig: any;
  hasPersonalEdition: boolean;
  loginAction: string;
  loginType: string;
  loginBindType: string;
  hasAppliedChannelOrg: boolean;
  orgChannel: string;
  channelUpdated: boolean;
}
interface UserSetting {
  authorityAndSettingMsg: boolean;
  shareMsg: boolean;
  esignMsg: boolean;
}
export interface UserState {
  userId: number;
  baseInfo: PersonalInfo;
  capacity: string;
  availableSpace: string;
  size: string;
  querySpaceTimestamp: number;
  hasPersonalSpace: boolean;
  defaultRoleId: number;
  roles: SMH.Role[];
  rolesLoding: boolean;
  messageNumber: number | string;
  availablePercent: number;
  hasPersonalEdition: boolean;
  userAccount: UserAccount;
  userSetting: UserSetting;
  approvedAgreement: boolean;
}
interface ActionParam {
  commit: Commit;
  state: UserState;
}
const defaultUserAccount = {
  userId: -1,
  isNewUser: 'unknown',
  isOrgNewUser: 'unknown',
  isLogined: false,
  remember: false,
  multiOrg: false,
  organizationId: -1,
  expired: false,
  editionFlag: '',
  editionConfig: {},
  hasPersonalEdition: false,
  loginAction: 'login',
  loginType: ConstantConfig.onlyPasswordLogin ? 'loginWithPassword' : 'loginWithPhone',
  loginBindType: '',
  hasAppliedChannelOrg: true,
  orgChannel: '',
  channelUpdated: false,
};
const defaultUserSetting = {
  authorityAndSettingMsg: true,
  shareMsg: true,
  esignMsg: true,
};
const user: StoreOptions<UserState> = {
  state: {
    userId: -1,
    baseInfo: {
      userId: -1,
      accountName: '',
      nickname: '',
      email: '',
      phoneNumber: '',
      avatar: '',
      role: 'unknown',
      comment: '',
      enabled: true,
      isTemporary: false,
      allowPersonalSpace: true,
      canManageTeams: [],
      thirdPartyAuthList: {
        meeting: false,
        wechat: false,
      },
    },
    // 已分配的空间大小
    capacity: '',
    // 可用空间大小
    availableSpace: '',
    // 查询使用空间的时间戳
    querySpaceTimestamp: 0,
    // 是否拥有个人空间
    hasPersonalSpace: true,
    // 个人空间已使用空间大小
    size: '',
    // 可用的内存比例
    availablePercent: 0,
    defaultRoleId: 0,
    roles: [],
    rolesLoding: false,
    messageNumber: 0,
    // 是否有个人版账号
    hasPersonalEdition: false,
    // 账号相关，和@/renderer/scripts/utils/user/index同步
    userAccount: defaultUserAccount,
    userSetting: defaultUserSetting, // 用户设置相关
    approvedAgreement: false, // 登录时是否同意协议
  },

  mutations: {
    SET_ROLES: (state: UserState, roles: SMH.Role[]): void => {
      state.roles = roles.sort((a, b) => Number(a.isCustom) - Number(b.isCustom));
    },
    SET_DEFAULT_ROLE_ID: (state: UserState, roleId: number): void => {
      state.defaultRoleId = roleId;
    },
    SET_USERID: (state: UserState, userId: number): void => {
      state.userId = userId;
      state.baseInfo.userId = userId;
    },
    SET_BASEINFO: (state: UserState, userBaseInfo: PersonalInfo): void => {
      state.baseInfo = {
        ...state.baseInfo,
        ...userBaseInfo,
      };
    },
    SET_CAPACITY: (state: UserState, capacity: string): void => {
      state.capacity = capacity;
    },
    SET_AVAILABLE_SPACE: (state: UserState, availableSpace: string): void => {
      state.availableSpace = availableSpace;
    },
    SET_AVAILABLE_PERCENT: (state: UserState, availablePercent: number): void => {
      state.availablePercent = availablePercent;
    },
    SET_SIZE: (state: UserState, size: string): void => {
      state.size = size;
    },
    SET_QUERY_TIMESTAMP: (state: UserState, querySpaceTimestamp: number): void => {
      state.querySpaceTimestamp = querySpaceTimestamp;
    },
    SET_PERSONAL_SPACE: (state: UserState, hasPersonalSpace: boolean): void => {
      state.hasPersonalSpace = hasPersonalSpace;
    },
    SET_MESSAGE_NUMBER: (state: UserState, messageNumber: number | string): void => {
      state.messageNumber = messageNumber;
      ipc.send(IpcEvents.MESSAGE_NUMBER, messageNumber);
    },
    SET_HAS_PERSONAL_EDITION(state: UserState, hasPersonalEdition: boolean) {
      state.hasPersonalEdition = hasPersonalEdition;
    },
    SET_USER_ACCOUNT(state: UserState, userAccount: UserAccount) {
      state.userAccount = {
        ...state.userAccount,
        ...userAccount,
      };
      ipc.send(IpcEvents.UPDATE_MENU_DATA, {
        event: 'login',
        param: state.userAccount.isLogined,
      });
    },
    SET_INIT_USER_ACCOUNT(state: UserState) {
      state.userAccount = defaultUserAccount;
    },
    SET_USER_SETTING(state: UserState, userSetting: UserSetting) {
      if (
        userSetting.authorityAndSettingMsg === undefined ||
        userSetting.shareMsg === undefined ||
        userSetting.esignMsg === undefined
      ) {
        return;
      }
      state.userSetting = {
        ...state.userSetting,
        ...userSetting,
      };
    },
    SET_APPROVED_AGREEMENT(state: UserState, val: boolean) {
      state.approvedAgreement = val;
    },
  },
  getters: {
    orgId: (state: UserState) => state.userAccount.organizationId,
    orgExpired: (state: UserState) => state.userAccount.expired,
    userId: (state: UserState) => state.userAccount.userId,
    isLogined(state: UserState) {
      // 避免初始化store的isLogined里信息和user里的isLogined不同步
      const { isLogined } = userIns.getUserInfoSync();
      if (state.userAccount.isLogined !== isLogined) {
        state.userAccount.isLogined = isLogined as boolean;
      }
      return state.userAccount.isLogined;
    },
    loginType: (state: UserState) => state.userAccount.loginType,
    loginAction: (state: UserState) => state.userAccount.loginAction,
    loginBindType: (state: UserState) => state.userAccount.loginBindType,
    remember: (state: UserState) => state.userAccount.remember,
    multiOrg: (state: UserState) => state.userAccount.multiOrg,
    editionFlag: (state: UserState) => state.userAccount.editionFlag,
    editionConfig: (state: UserState) => state.userAccount.editionConfig,
    hasPersonalEdition: (state: UserState) => state.userAccount.hasPersonalEdition,
    hasAppliedChannelOrg: (state: UserState) => state.userAccount.hasAppliedChannelOrg,
    orgChannel: (state: UserState) => state.userAccount.orgChannel || '',
    orgChannelEdition: (state: UserState) => {
      if (state.userAccount.orgChannel) {
        return channelEditionMap[state.userAccount.orgChannel] || '';
      }
      return '';
    },
    channelUpdated: (state: UserState) => state.userAccount.channelUpdated || false,
    isNewUser: (state: UserState) => state.userAccount.isNewUser,
    isOrgNewUser: (state: UserState) => state.userAccount.isOrgNewUser,
    userAccount: (state: UserState) => state.userAccount,

    userBaseInfo: (state: UserState) => state.baseInfo,
    allRoles: (state: UserState) => state.roles,
    defaultRoleId: (state: UserState) => state.defaultRoleId,
    /**
     * @description 获取用户权限信息，如上传者、编辑者
     */
    roles: (state: UserState) => state.roles.filter(role => !role.isOwner),
    /**
     * @description 除去所有者和禁止访问者的其他角色列表
     */
    rolesWithoutNoAccess: (state: UserState) => state.roles.filter(role => !role.isOwner && !isDisabledAllAuth(role)),
    rolesWithoutNoAccessMap: (state: UserState, getters) => {
      return new Map(getters.rolesWithoutNoAccess.map((role: SMH.Role) => [role.id, role]));
    },
    // 禁止访问权限
    roleWithNoAccess: (state: UserState) => state.roles.filter(role => isDisabledAllAuth(role))[0],
    roleIdToNameMap(state: UserState): Record<number, string> {
      const map: Record<number, string> = {};
      state.roles.forEach(item => {
        map[item.id] = item.name;
      });
      return map;
    },
    ownerRole: (state: UserState) => {
      const owner = state.roles.filter(role => role.isOwner);
      return owner[0];
    },
    availablePercent(state: UserState): number {
      return state.availablePercent;
    },
    operatorRole: (state: UserState) => {
      const operator = state.roles.filter((role: SMH.Role) => role.name === i18n_t('操作者'));
      return operator[0];
    },
    editorRole: (state: UserState) => {
      const operator = state.roles.filter((role: SMH.Role) => role.name === i18n_t('编辑者'));
      return operator[0];
    },
    /**
     * 获取用户角色信息
     * @description
     * 若 superAdmin 或 admin 同时为 teamAdmin,则认为是 superAdmin 或 admin
     * 若是teamAdmin, 只能代表用户是某一个团队的团队管理员，不一定是你现在关注的团队的团队管理员
     * @warning 若需要在协作空间中判断，当前用户是否是某一个协作群组的群主，则这个getter是无效的。
     * @returns RoleMap
     */
    userRole: (state: UserState, getters: GetterTree<UserState, any>, rootState, rootGetters) => {
      const { role, canManageTeams } = state.baseInfo;
      // role初始化为unknow 所以需要加判断
      if (!role || role === 'unknown') {
        return null;
      }
      if (['admin', 'superAdmin'].includes(role)) {
        return {
          admin: RoleMap.Admin,
          superAdmin: RoleMap.SuperAdmin,
        }[role as 'admin' | 'superAdmin'];
      }
      //  企业空间特殊判断一下，有需要精确根据teamId来进行校验
      const teamId = Number(rootGetters.getRoute.query?.teamId);
      if (canManageTeams?.length && teamId) {
        const isTeamAdmin = canManageTeams.includes(teamId);
        if (isTeamAdmin) {
          return RoleMap.TeamAdmin;
        }
        return RoleMap.User;
      }
      if (canManageTeams?.length) {
        return RoleMap.TeamAdmin;
      }
      return RoleMap.User;
    },
    capacity: (state: UserState) => state.capacity,
    availableSpace: (state: UserState) => state.availableSpace,
    userFileSize: (state: UserState) => state.size,
    querySpaceTimestamp: (state: UserState) => state.querySpaceTimestamp,
    hasPersonalSpace: (state: UserState) => state.hasPersonalSpace,
    messageNumber: (state: UserState) => state.messageNumber,
    authorityAndSettingMsg: (state: UserState) => state.userSetting.authorityAndSettingMsg,
    shareMsg: (state: UserState) => state.userSetting.shareMsg,
    esignMsg: (state: UserState) => state.userSetting.esignMsg,
    approvedAgreement: (state: UserState) => state.approvedAgreement,
  },
  actions: {
    async GetRoles({ commit, state }: ActionParam): Promise<SMH.Role[]> {
      state.rolesLoding = true;
      try {
        const { data } = await smh.userAPI.getRoleList();
        const defaultRoleId = data.find(item => item.isDefault)?.id || data[0].id || undefined;
        commit('SET_ROLES', data);
        commit('SET_DEFAULT_ROLE_ID', defaultRoleId);
        state.rolesLoding = false;
        return data;
      } catch (error) {
        commit('SET_ROLES', []);
        commit('SET_ADMIN_ROLES', []);
        commit('SET_DEFAULT_ROLE_ID', undefined);
        state.rolesLoding = false;
        return [];
      }
    },
    // 登录获得userToken
    async loginSMS({ commit, state }: ActionParam, params: SMH.LoginParams): Promise<CoFile.LoginSMSData> {
      let result: CoFile.LoginSMSData;
      try {
        const res = await smh.api.login({
          ...params,
          flag: !IS_PRIVATE ? 1 : '',
          ...(!IS_PRIVATE && {
            domain: params.domain,
          }),
        });
        result = {
          organizations: [],
          userId: -1,
          userToken: '',
          userTokenExpiresIn: 0,
          userTokenStart: 0,
          isNewUser: 'unknown',
          hasAppliedChannelOrg: true,
        };
        const { data } = res;
        const { userId, userToken, expiresIn, organizations, isNewUser, hasAppliedChannelOrg } = data;
        // eslint-disable-next-line no-nested-ternary
        const isNewUserStr = isNewUser === undefined ? 'unknown' : isNewUser ? 'yes' : 'no';
        commit('SET_USERID', userId);
        result = {
          organizations,
          userId,
          hasAppliedChannelOrg,
          userToken,
          userTokenExpiresIn: expiresIn,
          userTokenStart: new Date().getTime() / 1000,
          isNewUser: isNewUserStr,
        };
      } catch (e: any) {
        result = {
          ...e,
          hasError: true,
        };
      }
      return result;
    },
    // 注册登录
    async registerWithPhone({ commit, state }: ActionParam, params: SMH.RegisterParams): Promise<CoFile.LoginSMSData> {
      let result: CoFile.LoginSMSData = {
        organizations: [],
        userId: -1,
        userToken: '',
        userTokenExpiresIn: 0,
        userTokenStart: 0,
        isNewUser: 'unknown',
        hasAppliedChannelOrg: true,
      };
      try {
        const res = await smh.api.register({
          ...params,
        });
        const { data } = res;
        const { userId, userToken, expiresIn, organizations, isNewUser, hasAppliedChannelOrg } = data;
        // eslint-disable-next-line no-nested-ternary
        const isNewUserStr = isNewUser === undefined ? 'unknown' : isNewUser ? 'yes' : 'no';
        commit('SET_USERID', userId);
        result = {
          organizations,
          userId,
          hasAppliedChannelOrg,
          userToken,
          userTokenExpiresIn: expiresIn,
          userTokenStart: new Date().getTime() / 1000,
          isNewUser: isNewUserStr,
        };
      } catch (e: any) {
        result = {
          ...e,
          hasError: true,
        };
      }
      return result;
    },
    // 获取用户基本信息
    async getUserBaseInfo({ commit, state }: ActionParam, pf = ''): Promise<void> {
      try {
        const { userId } = userIns.getUserInfo();
        const { data } = await smh.userAPI.getUser({
          userId,
          pf,
        });
        const {
          id,
          accountName,
          nickname,
          email,
          countryCode,
          phoneNumber,
          avatar,
          role,
          comment,
          enabled,
          allowPersonalSpace,
          canManageTeams,
          thirdPartyAuthList,
          isNewUser,
          within7DaysOrgUser,
          hasAppliedChannelOrg,
          isFirstLogin,
          isTemporary,
        } = data;
        commit('SET_BASEINFO', {
          userId: id,
          accountName,
          nickname,
          email,
          countryCode,
          phoneNumber,
          avatar,
          role,
          comment,
          enabled,
          allowPersonalSpace,
          canManageTeams,
          thirdPartyAuthList,
          isTemporary,
        });
        // eslint-disable-next-line no-nested-ternary
        const isNewUserStr = isNewUser === undefined ? 'unknown' : isNewUser ? 'yes' : 'no';
        // eslint-disable-next-line no-nested-ternary
        const isOrgNewUserStr = within7DaysOrgUser === undefined ? 'unknown' : within7DaysOrgUser ? 'yes' : 'no';
        userIns.setChannelApplied(hasAppliedChannelOrg as boolean);
        userIns.setUserRole(role);
        userIns.setIsNewUser(isNewUserStr);
        userIns.setIsOrgNewUser(isOrgNewUserStr);
        // get_user_info只为触发used事件，实际灯塔里不需要分析get_user_info
        reporter.report('get_user_info');
        // 超管首次登录需要埋点上报
        if (isFirstLogin && role === 'superAdmin') {
          reporter.report('new_login');
        }
      } catch (e: any) {
        tips.error(e);
      }
    },
    // 查询是否有个人空间
    async checkPersonalSpace({ commit, state }: ActionParam): Promise<void> {
      try {
        const res = await smh.userAPI.hasPersonalSpace();
        const { data } = res;
        commit('SET_PERSONAL_SPACE', data.hasPersonalSpace);
        commit('SET_CAPACITY', data.capacity || '');
        commit('SET_AVAILABLE_SPACE', data.availableSpace || '');
        commit('SET_SIZE', data.size || '');
        commit('SET_AVAILABLE_PERCENT', (Number(data.availableSpace) / Number(data.capacity)) * 100);
      } catch (e: any) {
        if (e.code !== 'OrganizationServiceTimeExpired') {
          tips.error(e);
        }
      }
    },
    // 查询用户所属企业
    async getOrgList({ commit, state }: ActionParam): Promise<void> {
      try {
        const res = await smh.userAPI.getUserOrg();
        const hasPersonalEdition = res.data.find(
          item => item?.extensionData?.editionConfig?.editionFlag === 'personal',
        );
        commit('SET_USER_ACCOUNT', {
          hasPersonalEdition,
        });
        commit('SET_USER_ACCOUNT', {
          multiOrg: res.data.length > 1,
        });
      } catch (e) {
        logger.ui.error(e);
      }
    },
    // 更新未读的消息通知数
    UPDATE_MESSAGE_NUMBER: debounce(async ({ commit, state }: ActionParam): Promise<void> => {
      try {
        const { data } = await smh.userAPI.listMessage({
          type: 0,
        });
        commit('SET_MESSAGE_NUMBER', data.unreadMessageNumber);
      } catch (e) {
        logger.ui.error(e);
      }
    }),
    // 查询用户设置
    async getUserSetting({ commit, state }: ActionParam): Promise<void> {
      try {
        const { data } = await smh.userAPI.getSettingConfig();
        commit('SET_USER_SETTING', {
          authorityAndSettingMsg: data.receiveMessageConfig?.authorityAndSettingMsg,
          shareMsg: data.receiveMessageConfig?.shareMsg,
          esignMsg: data.receiveMessageConfig?.esignMsg,
        });
      } catch (e: any) {
        logger.ui.error(e);
      }
    },
  },
};
export default user;
